import { track } from '../lib/funnel';
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { modeId } from '../lib/profiles';
import SECTOR from '../lib/sectorTours.json';
import PANEL from '../lib/panelTours.json';
import { uiZoom } from '../lib/uiScale';
import { speak } from '../lib/a11y';
import { GraduationCap } from 'lucide-react';
import SpeakBtn from './SpeakBtn';
import { playTts, stopTts, tourText, ttsFind } from '../lib/helpAudio';

export const TOUR_KEY = 'giss_tour_done';
const STEPS = ['mode-bar-btn', 'new-single-btn', 'fs-enter-btn', 'export-center', 'help-btn'];
const BUSY = '[data-testid="profile-close-btn"], [data-testid="place-picker"], .editor-backdrop, .gnss-backdrop, .prec-backdrop, .stc-overlay, body.start-open, [data-testid="track-sheet"], .ghost-cam, .ar-stake, .sig-ov, .stake-panel';
export const SECTOR_TOOLS = {
  detect_water: ['fs-tool-leak', 'fs-tool-logger', 'fs-tool-corr', 'fs-tool-gmic'],
  detect_gas: ['fs-tool-leak', 'fs-tool-kks', 'fs-tool-kks-plan', 'fs-tool-track'],
  detect_cable: ['fs-tool-fault', 'fs-tool-cfault', 'fs-tool-locate', 'fs-tool-gnss'],
  detect_sewer: ['fs-tool-pipe', 'fs-tool-fitting', 'fs-tool-leak', 'fs-tool-photo'],
  detect_inhouse: ['fs-tool-leak', 'fs-tool-tracer', 'fs-tool-studio', 'fs-tool-photo'],
  detect_heat: ['fs-tool-leak', 'fs-tool-fault', 'fs-tool-tracer', 'fs-tool-pipe'],
  detect_roof: ['fs-tool-sketch', 'fs-tool-leak', 'fs-tool-moist', 'fs-tool-tracer'],
  detect_locate: ['fs-tool-locate', 'fs-tool-tracer', 'fs-tool-gnss', 'fs-tool-pipe'],
  pipe: ['fs-tool-pipe', 'fs-tool-fitting', 'fs-tool-pit', 'fs-tool-open-ends'],
  garden: ['fs-tool-garden', 'fs-tool-garden-rows', 'fs-tool-garden-land', 'fs-tool-track'],
  building: ['fs-tool-facade', 'fs-tool-studio', 'fs-tool-photo', 'fs-tool-gnss'],
  outdoor: ['fs-tool-track', 'fs-tool-xbear', 'fs-tool-proj', 'fs-tool-gpx'],
};
export const portalRoot = () => { const f = document.fullscreenElement; return f && f !== document.documentElement && f !== document.body ? f : document.body; };
const q = (id) => document.querySelector(`[data-testid="${id}"]`);
const secKey = (m) => `giss_tour_${m}`;
const ended = new Set();

// first-start choice: guided help with voice, without voice, or none
const VOICE_KEY = 'giss_tour_voice';
const tourVoice = () => localStorage.getItem(VOICE_KEY) === '1';
export const setTourHelp = (mode) => {
  localStorage.setItem(VOICE_KEY, mode === 'voice' ? '1' : '');
  if (mode !== 'no') return;
  [TOUR_KEY, 'giss_tour_map', ...Object.keys(SECTOR_TOOLS).map(secKey)].forEach((k) => localStorage.setItem(k, '1'));
};

export const restartTour = () => {
  localStorage.removeItem(TOUR_KEY);
  localStorage.removeItem('giss_tour_map');
  Object.keys(SECTOR_TOOLS).forEach((m) => localStorage.removeItem(secKey(m)));
  window.dispatchEvent(new Event('giss-tour'));
};

// restart only the tour of one area: start page, fullscreen map or tool list
// window tours: only on request, steps that are visible right now
const PANEL_STEPS = {
  lp: ['lp-tab-plan', 'lp-tab-deploy', 'lp-tab-collect', 'lp-name-input', 'lp-addr-input', 'lp-confirm-btn', 'lp-list', 'lp-start-round-btn', 'lp-share-round-btn', 'lp-round-list', 'lp-round-route-btn', 'lp-next-name', 'lp-dist', 'lp-nav-btn', 'lp-show-btn', 'lp-skip-btn', 'lp-set-name', 'lp-photo-btn', 'lp-set-btn', 'lc-filter', 'lc-next-name', 'lc-dist', 'lc-nav-btn', 'lc-noise-yes', 'lc-collect-btn', 'lc-missing-btn', 'lc-move-btn'],
  leak: ['object-name-input', 'object-depth-input', 'leak-calc', 'leak-photo-btn', 'leak-result', 'object-note-input', 'plan-status', 'object-sheet-help', 'object-sheet-close-btn'],
  report: ['report-banner', 'report-select', 'new-report-btn', 'report-address-input', 'report-route-btn', 'report-done-toggle', 'take-main-photo-btn', 'add-detail-photo-btn', 'add-note-photo-btn', 'add-video-btn', 'count-material-btn', 'export-center'],
};
let panelReq = null;
const readyPanel = () => {
  if (!panelReq) return null;
  const a = panelReq, ids = PANEL_STEPS[a].filter((id) => q(id)?.getClientRects().length);
  panelReq = null;
  return ids.length ? { key: `giss_tour_p_${a}`, ids } : null;
};
export const startTourHere = (area) => {
  if (PANEL_STEPS[area]) { panelReq = area; window.dispatchEvent(new Event('giss-tour')); return; }
  if (area === 'main') localStorage.removeItem(TOUR_KEY);
  else localStorage.setItem(TOUR_KEY, '1');
  if (area === 'map') localStorage.removeItem('giss_tour_map');
  if (area === 'sector') { localStorage.setItem('giss_tour_map', '1'); localStorage.removeItem(secKey(modeId())); ended.delete(secKey(modeId())); }
  window.dispatchEvent(new Event('giss-tour'));
};
export const hasSectorTour = () => !!SECTOR_TOOLS[modeId()];
export function HelpHere({ t, area, className = 'chip', compact = false }) {
  return (
    <button type="button" className={`${className} help-here-btn`} data-testid={`help-here-${area}-btn`} title={t.helpHereT} onClick={() => startTourHere(area)}>
      <GraduationCap size={compact ? 16 : 14} />{!compact && <span>{t.helpHere}</span>}
    </button>
  );
}

function useRect(id, on) {
  const [r, setR] = useState(null);
  useLayoutEffect(() => {
    if (!on) return undefined;
    // fullscreen tool list: scroll only the list (never the page); getBoundingClientRect is zoomed, scrollTop is not
    const toView = () => {
      const el = q(id), menu = el?.closest('.fs-menu'), bar = el?.closest('.map-top-controls');
      if (el?.closest('.lp-panel, .obj-sheet')) { el.scrollIntoView({ block: 'nearest' }); return; }
      if (menu) { const mb = menu.getBoundingClientRect(), eb = el.getBoundingClientRect(); menu.scrollTop += (eb.top - mb.top - (mb.height - eb.height) / 2) / uiZoom(); return; }
      // page first (target may be far below on the phone), then the horizontal map tool bar
      if (el && !document.querySelector('.map-fs')) {
        const b = el.getBoundingClientRect();
        if (b.top < 60 || b.bottom > window.innerHeight - 60) { el.scrollIntoView({ block: b.height > window.innerHeight * 0.5 ? 'start' : 'center' }); if (b.height > window.innerHeight * 0.5) window.scrollBy(0, -70); }
      }
      if (bar) { const bb = bar.getBoundingClientRect(), eb = el.getBoundingClientRect(); bar.scrollLeft += (eb.left - bb.left - (bb.width - eb.width) / 2) / uiZoom(); }
    };
    toView();
    const upd = () => { const b = q(id)?.getBoundingClientRect(), z = uiZoom(); setR(b ? { top: b.top / z, left: b.left / z, width: b.width / z, height: Math.min(b.height, window.innerHeight * 0.5) / z } : null); };
    const tm = setTimeout(() => { if (q(id)?.closest('.fs-menu, .map-top-controls')) toView(); upd(); }, 350), iv = setInterval(upd, 600);
    window.addEventListener('resize', upd); window.addEventListener('scroll', upd, true);
    return () => { clearTimeout(tm); clearInterval(iv); window.removeEventListener('resize', upd); window.removeEventListener('scroll', upd, true); };
  }, [id, on]);
  return r;
}

// waits until ready() returns a step list, then walks through it once (remembered under storeKey())
function Tour({ t, lang, ready, text, idOf, testid }) {
  const [run, setRun] = useState(null);
  const [step, setStep] = useState(0);
  const [tipH, setTipH] = useState(200);
  useEffect(() => {
    let iv;
    const arm = () => { clearInterval(iv); iv = setInterval(() => { const r = ready(); if (r) { clearInterval(iv); setStep(0); setRun(r); } }, 800); };
    arm();
    window.addEventListener('giss-tour', arm);
    return () => { clearInterval(iv); window.removeEventListener('giss-tour', arm); };
  }, [ready]);
  const ids = run?.ids || [];
  const r = useRect(ids[step], !!run);
  // voice mode: stored ElevenLabs recording if there is one, otherwise the device voice
  useEffect(() => {
    if (!run || !tourVoice()) return;
    const [a, b] = text(run, step), id = idOf(run, step), dev = () => speak(`${a}. ${b}`, true);
    ttsFind(lang, tourText(a, b)).then((h) => (h ? playTts(id, h, dev) : dev()));
  }, [run, step]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!run) return null;
  const end = () => { if (run.key === TOUR_KEY || run.key === 'giss_tour_map') track(step >= ids.length - 1 ? 'tour_done' : 'tour_skip'); localStorage.setItem(run.key, '1'); ended.add(run.key); setRun(null); window.speechSynthesis?.cancel(); stopTts(); };
  const next = () => (step === ids.length - 1 ? end() : setStep(step + 1));
  const [title, body] = text(run, step);
  const z = uiZoom(), vw = window.innerWidth / z, vh = window.innerHeight / z;
  // tip never covers the target: below if it fits (or has more room), else above; height capped to the free room
  const spB = r ? vh - (r.top + r.height + 14) - 8 : 0, spA = r ? r.top - 14 - 8 : 0;
  const below = !r || spB >= tipH || spB >= spA;
  const tipMax = r ? Math.max(120, below ? spB : spA) : vh * 0.42, tipTop = r ? (below ? r.top + r.height + 14 : r.top - 14) : vh / 3;
  const measure = (el) => { if (el && Math.abs(el.scrollHeight - tipH) > 4) setTipH(el.scrollHeight); };
  const tw = Math.min(320, vw - 20), tipLeft = r ? Math.max(10, Math.min(r.left - (r.left > vw - tw - 20 ? tw + 10 - r.width : 0), vw - tw - 10)) : 10;
  return createPortal(
    <div className="tour-layer" data-testid={`${testid}-layer`}>
      {r && <div className="tour-spot" style={{ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 }} />}
      <div className={`tour-tip ${below ? '' : 'tour-up'}`} ref={measure} style={{ top: tipTop, left: tipLeft, maxHeight: tipMax, overflowY: 'auto' }} data-testid={`${testid}-tip`}>
        <small className="mono" data-testid={`${testid}-progress`}>{step + 1} / {ids.length}</small>
        <div className="tour-head"><b data-testid={`${testid}-title`}>{title}</b><SpeakBtn t={t} id={idOf(run, step)} lang={lang} text={tourText(title, body)} testid={`${testid}-speak-btn`} /></div>
        <p data-testid={`${testid}-body`}>{body}</p>
        <div className="row">
          <button className="btn btn-outline btn-sm" data-testid={`${testid}-skip-btn`} onClick={end}>{t.tourSkip}</button>
          {step > 0 && <button className="btn btn-outline btn-sm" data-testid={`${testid}-back-btn`} onClick={() => setStep(step - 1)}>{t.tourBack}</button>}
          <button className="btn btn-primary btn-sm" data-testid={`${testid}-next-btn`} onClick={next}>{step === ids.length - 1 ? t.tourDone : t.tourNext}</button>
        </div>
      </div>
    </div>,
    portalRoot(),
  );
}

const readyMain = () => (!localStorage.getItem(TOUR_KEY) && localStorage.getItem('giss_setup_done') && !document.querySelector(BUSY) && q(STEPS[0]) ? { key: TOUR_KEY, ids: STEPS } : null);
export const MAP_STEPS = ['fs-exit-btn', 'map-compass-btn', 'fs-quick-bar', 'fs-tools-tab', 'map-ui-settings-btn', 'map-vis-btn', 'map-fit-all-btn', 'map-layer-select', 'map-ui-toggle-btn', 'fs-follow-btn', 'map-gps-btn', 'map-target-search-btn', 'fs-glove-btn'];
const MAP_KEY = 'giss_tour_map';
const readyMap = () => {
  if (localStorage.getItem(MAP_KEY) || !localStorage.getItem(TOUR_KEY) || !document.querySelector('.map-fs') || document.querySelector('.fs-drawer.open') || document.querySelector(BUSY)) return null;
  const ids = MAP_STEPS.filter((id) => q(id)?.getClientRects().length);
  return ids.length ? { key: MAP_KEY, ids } : null;
};
const readySector = () => {
  const m = modeId();
  if (!SECTOR_TOOLS[m] || ended.has(secKey(m)) || localStorage.getItem(secKey(m)) || !localStorage.getItem(TOUR_KEY) || !localStorage.getItem(MAP_KEY) || !document.querySelector('.fs-drawer.open')) return null;
  const all = SECTOR_TOOLS[m].map((id, i) => [id, i]).filter(([id]) => q(id));
  return all.length ? { key: secKey(m), mode: m, ids: all.map((a) => a[0]), idx: all.map((a) => a[1]) } : null;
};

export default function TourGuide({ t, lang }) {
  return (
    <>
      <Tour t={t} lang={lang} ready={readyMain} testid="tour" idOf={(_, s) => `tour:main:${s + 1}`} text={(_, s) => [t[`tour${s + 1}T`], t[`tour${s + 1}B`]]} />
      <Tour t={t} lang={lang} ready={readyMap} testid="map-tour" idOf={(run, s) => `tour:map:${run.ids[s]}`} text={(run, s) => t.mapTour[run.ids[s]] || ['', '']} />
      <Tour t={t} lang={lang} ready={readyPanel} testid="panel-tour" idOf={(run, s) => `tour:panel:${run.ids[s]}`} text={(run, s) => (PANEL[lang] || PANEL.en)[run.ids[s]] || ['', '']} />
      <Tour t={t} lang={lang} ready={readySector} testid="sector-tour" idOf={(run, s) => `tour:sec:${run.mode}:${run.idx[s]}`} text={(run, s) => (SECTOR[lang] || SECTOR.de)[run.mode]?.[run.idx[s]] || SECTOR.de[run.mode]?.[run.idx[s]] || ['', '']} />
    </>
  );
}
